import { useMemo } from "react";
import { createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel } from "@tanstack/react-table";
import { type BusinessSubscription } from "lens-platform-sdk";
import { action } from "@k8slens/lds-icons";

import { type PendingSubscriptionInvitation } from "src/services/getInvitationState";
import { ActivationLinkInvitation } from "src/hooks/useGetActivationLinkInvitation";
import { trimURL } from "src/services/trimURL";
import { useTrackedTableSort } from "src/hooks/useTrackedTableSort";

import Table from "src/components/Table/Table";
import { ContextMenuItem } from "src/components/ContextMenu/ContextMenu";
import { CopyToClipboard } from "src/components/CopyToClipboard/CopyToClipboard";
import { dateColDef } from "src/components/TableCells/DateCell";
import ContextMenuCell, { contextMenuColDef } from "../TableCells/ContextMenuCell";
import { subscriptionColDef } from "src/components/TableCells/SubscriptionCell";

const { DeleteIcon } = action;
const pendingTableColumnHelper = createColumnHelper<PendingSubscriptionInvitation | ActivationLinkInvitation>();

const caption = "Pending seats";

interface Props {
  businessSubscriptions: BusinessSubscription[];
  pendingSubscriptionInvitations: Array<ActivationLinkInvitation | PendingSubscriptionInvitation>;
  onRemoveBusinessInvitation?: (invitation: ActivationLinkInvitation | PendingSubscriptionInvitation) => void;
  loading?: boolean;
}

const PendingUserTable = ({
  businessSubscriptions,
  pendingSubscriptionInvitations,
  onRemoveBusinessInvitation,
  loading,
}: Props) => {
  const handleRemovePendingUser = (invitation: ActivationLinkInvitation | PendingSubscriptionInvitation) => {
    onRemoveBusinessInvitation && onRemoveBusinessInvitation(invitation);
  };

  const pendingTableColumns = [
    pendingTableColumnHelper.accessor("email", {
      id: "assignMethod",
      header: () => <span>Assign method</span>,
      cell: ({ row }) => {
        const pendingInvitation = row.original;

        const value = "link" in pendingInvitation ? "Activation Link" : "email";

        return <div>{value}</div>;
      },
      meta: { primary: false, text: "default" },
    }),
    pendingTableColumnHelper.accessor("email", {
      header: () => <span>Details</span>,
      cell: ({ row }) => {
        const pendingInvitation = row.original;
        const linkType = "link" in pendingInvitation;

        const value = linkType ? trimURL(pendingInvitation.link) : pendingInvitation.email;

        return (
          <div>
            {value}
            {linkType && (
              <>
                {" "}
                <CopyToClipboard text={pendingInvitation.link} />
              </>
            )}
          </div>
        );
      },
      meta: { primary: true, text: "default" },
    }),
    pendingTableColumnHelper.accessor("subscription", { ...subscriptionColDef }),
    pendingTableColumnHelper.accessor("createdAt", {
      ...dateColDef,
      header: () => <span>Invitation Date</span>,
    }),
    pendingTableColumnHelper.accessor("id", {
      ...contextMenuColDef,
      cell: ({ row }) => (
        <ContextMenuCell toggleButtonProps={{ title: "Toggle context menu" }}>
          <ContextMenuItem label="Remove" icon={DeleteIcon} onClick={() => handleRemovePendingUser(row.original)} />
        </ContextMenuCell>
      ),
    }),
  ];
  const pendingUsers = useMemo<Array<PendingSubscriptionInvitation | ActivationLinkInvitation>>(
    () =>
      pendingSubscriptionInvitations.map((businessInvitation) => ({
        ...businessInvitation,
        subscription: businessSubscriptions.find(({ id }) => id === businessInvitation.subscriptionId),
      })),
    [businessSubscriptions, pendingSubscriptionInvitations],
  );
  const { sorting, onSortingChange } = useTrackedTableSort({ table: caption });

  const pendingUserTable = useReactTable({
    data: pendingUsers,
    columns: pendingTableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange,
  });
  const pendingUserRowModel = pendingUserTable.getRowModel();
  const pendingUserRows = useMemo(() => pendingUserRowModel.rows, [pendingUserRowModel]);

  return (
    <Table<PendingSubscriptionInvitation | ActivationLinkInvitation>
      caption={caption}
      loading={loading}
      rows={pendingUserRows}
      columns={pendingUserTable.getAllColumns()}
      headerGroups={pendingUserTable.getHeaderGroups()}
      noDataText="No pending seats on your Lens Business ID!"
    />
  );
};

export default PendingUserTable;
